import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { CalendarDays, CheckCircle2, Clock, Cloud, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { FadeUp } from "@/components/motion/Reveal";
import { createMeetingBooking } from "@/lib/api";
import SmsConsent from "@/components/SmsConsent";

const TIME_SLOTS = [
  "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
  "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM",
];

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export default function BookMeeting() {
  const location = useLocation();
  const prefill = location.state && location.state.prefilled ? location.state : null;
  const [form, setForm] = useState({
    name: prefill?.name || "",
    email: prefill?.email || "",
    phone: prefill?.phone || "",
    notes: "",
  });
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Please share your name.");
    if (!EMAIL_RE.test(form.email.trim())) return toast.error("Please enter a valid email.");
    if (!date) return toast.error("Please pick a date.");
    if (!time) return toast.error("Please pick a time.");
    setSubmitting(true);
    try {
      await createMeetingBooking({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        meeting_date: format(date, "yyyy-MM-dd"),
        meeting_time: time,
        notes: form.notes.trim(),
      });
      setDone(true);
      toast.success("Meeting request sent — we'll confirm shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't book — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <section id="book" data-testid="book-section" className="relative py-24 lg:py-32 bg-forest text-cream overflow-hidden">
      {/* Ambient sky */}
      <div aria-hidden className="absolute inset-0 opacity-[0.08] bg-[radial-gradient(circle_at_20%_20%,#38BDF8_0%,transparent_45%),radial-gradient(circle_at_80%_80%,#0284C7_0%,transparent_50%)]" />
      <div className="relative mx-auto max-w-6xl px-6 grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
        {/* Left copy */}
        <div>
          <FadeUp>
            <div className="inline-flex items-center gap-2 rounded-full border border-cream/20 bg-forest-700/60 px-4 py-1.5 text-[11px] uppercase tracking-[0.16em] font-bold text-cream/80">
              <Cloud className="h-3.5 w-3.5 text-terracotta" /> Book a meeting
            </div>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl sm:text-5xl leading-[1.05] tracking-tight">
              Pick a time.<br />
              Talk to a real advisor.
            </h2>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="mt-5 text-cream/70 text-lg max-w-md leading-relaxed">
              No robots, no phone-tree loops. Choose a slot that works for you and a licensed Advanced Life Policies advisor
              will call to walk you through refinancing your life insurance.
            </p>
          </FadeUp>
          <FadeUp delay={0.3}>
            <ul className="mt-8 space-y-3 text-cream/80 text-sm">
              {[
                "15-minute intro call",
                "No commitment, ever",
                "Real human, not a script",
              ].map((line) => (
                <li key={line} className="flex items-center gap-3">
                  <span className="grid place-items-center h-6 w-6 rounded-full bg-sage/20">
                    <CheckCircle2 className="h-3.5 w-3.5 text-sage-light" />
                  </span>
                  {line}
                </li>
              ))}
            </ul>
          </FadeUp>
        </div>

        {/* Right form / success */}
        <FadeUp delay={0.15}>
          {done ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              className="rounded-3xl bg-cream text-forest border border-cream/30 shadow-2xl p-10 text-center"
              data-testid="book-success"
            >
              <span className="grid place-items-center w-16 h-16 rounded-full bg-sage/20 mx-auto">
                <CheckCircle2 className="h-8 w-8 text-forest" />
              </span>
              <h3 className="mt-6 font-serif text-3xl">You&apos;re booked in!</h3>
              <p className="mt-3 text-forest/70">
                Thanks {form.name.split(" ")[0]} — we&apos;ll send a confirmation to <strong>{form.email}</strong> shortly with
                the meeting details for <strong>{format(date, "EEEE, MMM d")} at {time}</strong>.
              </p>
            </motion.div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="book-form"
              className="rounded-3xl bg-cream text-forest shadow-2xl p-7 sm:p-8 space-y-4"
            >
              {prefill && prefill.fromApp && (
                <div className="rounded-xl bg-sage/15 border border-sage/40 px-4 py-3 flex items-center gap-3" data-testid="book-thanks-banner">
                  <span className="grid place-items-center h-8 w-8 rounded-full bg-sage/30 text-forest shrink-0">
                    <CheckCircle2 className="h-4 w-4" />
                  </span>
                  <p className="text-sm text-forest leading-snug">
                    <span className="font-semibold">Thanks, {String(form.name).split(" ")[0]}!</span> Your request is in — pick a time below and we&apos;ll send you an instant Google Meet invite.
                  </p>
                </div>
              )}
              {prefill ? (
                <div className="rounded-xl bg-sand/70 border border-forest/10 px-4 py-3 flex items-center gap-3" data-testid="book-prefill-summary">
                  <span className="grid place-items-center h-9 w-9 rounded-full bg-forest/10 text-forest">
                    <CheckCircle2 className="h-4 w-4" />
                  </span>
                  <div className="text-sm min-w-0">
                    <p className="font-semibold text-forest truncate">Booking for {form.name}</p>
                    <p className="text-forest/55 text-xs truncate">{form.email}{form.phone ? ` · ${form.phone}` : ""}</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Name">
                      <Input
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="Your name"
                        data-testid="book-name"
                        className="h-12 rounded-xl bg-white border-forest/15 text-forest"
                      />
                    </Field>
                    <Field label="Phone">
                      <Input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="(917) 555-0123"
                        data-testid="book-phone"
                        className="h-12 rounded-xl bg-white border-forest/15 text-forest"
                      />
                    </Field>
                  </div>
                  <Field label="Email">
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="you@example.com"
                      data-testid="book-email"
                      className="h-12 rounded-xl bg-white border-forest/15 text-forest"
                />
              </Field>
                </>
              )}
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Preferred date">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid="book-date-trigger"
                        className="h-12 w-full rounded-xl bg-white border border-forest/15 px-3 flex items-center gap-2 text-left text-forest text-sm hover:border-forest/30 transition-colors"
                      >
                        <CalendarDays className="h-4 w-4 text-forest/50" />
                        {date ? format(date, "EEE, MMM d yyyy") : <span className="text-forest/40">Pick a date</span>}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="p-0 w-auto" data-testid="book-date-pop">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={(d) => {
                          const day = d.getDay();
                          return d < today || day === 0 || day === 6; // weekdays only
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </Field>
                <Field label="Preferred time">
                  <Select value={time} onValueChange={setTime}>
                    <SelectTrigger className="h-12 rounded-xl bg-white border-forest/15 text-forest" data-testid="book-time-trigger">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-forest/50" />
                        <SelectValue placeholder="Pick a time" />
                      </div>
                    </SelectTrigger>
                    <SelectContent data-testid="book-time-content">
                      {TIME_SLOTS.map((t) => (
                        <SelectItem key={t} value={t} data-testid={`book-time-${t.replace(/\s|:/g, "")}`}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label="Anything we should know? (optional)">
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="e.g. I'd like to discuss whole life vs. term."
                  data-testid="book-notes"
                  rows={3}
                  className="rounded-xl bg-white border-forest/15 text-forest resize-none"
                />
              </Field>

              <Button
                type="submit"
                disabled={submitting}
                data-testid="book-submit"
                className="w-full h-13 py-3 rounded-full bg-terracotta hover:bg-terracotta-dark text-cream text-base font-semibold shadow-lg transition-all duration-300 hover:-translate-y-0.5 active:scale-95"
              >
                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Book my meeting"}
              </Button>
              <p className="text-center text-xs text-forest/55">
                We&apos;ll email a confirmation and calendar invite once your slot is confirmed.
              </p>
              <SmsConsent className="text-center pt-1" />
            </form>
          )}
        </FadeUp>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.14em] font-bold text-sage mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
